import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import axiosInstance from "../../../Axiosinstance";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return `₹${Number(value).toLocaleString("en-IN")}`;
};

const PAGE_SIZE = 30;

const isSuperAdminUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return user?.role === "superadmin";
  } catch (error) {
    return false;
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CompanyWallet = () => {
  const { company_uid } = useParams();
  const canRecharge = isSuperAdminUser();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [recharging, setRecharging] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchWallet = useCallback(async () => {
    if (!company_uid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      const { data } = await axiosInstance.get(
        `/api/companies/${company_uid}/wallet?${params}`
      );
      if (data?.status) {
        const responsePage = data.data?.pagination?.page || 1;
        setWallet(data.data);
        setTotalPages(data.data?.pagination?.totalPages || 1);
        if (responsePage !== page) setPage(responsePage);
      } else {
        toast.error(data?.message || "Unable to fetch wallet");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Unable to fetch company wallet"
      );
    } finally {
      setLoading(false);
    }
  }, [company_uid, page]);

  useEffect(() => {
    setPage(1);
  }, [company_uid]);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  const handleRecharge = async (e) => {
    e.preventDefault();
    const amount = Number(rechargeAmount);
    if (!Number.isInteger(amount) || amount <= 0) {
      toast.error("Enter a positive whole number amount");
      return;
    }

    setRecharging(true);
    try {
      const { data } = await axiosInstance.patch(
        `/api/companies/${company_uid}/recharge`,
        { amount }
      );
      if (data?.status) {
        toast.success(data.message || "Wallet recharged successfully");
        setRechargeAmount("");
        if (page !== 1) {
          setPage(1);
        } else {
          await fetchWallet();
        }
      } else {
        toast.error(data?.message || "Recharge failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Recharge failed");
    } finally {
      setRecharging(false);
    }
  };

  const history = wallet?.transaction_history || [];

  return (
    <div className="page-content">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-1">Transaction History</h4>
          <div className="text-muted">
            {wallet?.company_name || company_uid}
          </div>
        </Col>
        {canRecharge && (
          <Col xs="auto" className="d-flex gap-2">
            <Button
              color="light"
              tag={Link}
              to={`/companymanagement/${company_uid}`}
            >
              Back to Company
            </Button>
            <Button color="light" tag={Link} to="/companymanagement">
              All Companies
            </Button>
          </Col>
        )}
      </Row>

      {loading && !wallet ? (
        <Card>
          <CardBody className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
          </CardBody>
        </Card>
      ) : !wallet ? (
        <Card>
          <CardBody className="text-muted">Wallet details unavailable</CardBody>
        </Card>
      ) : (
        <>
          <Row>
            <Col md={canRecharge ? "4" : "6"}>
              <Card>
                <CardBody>
                  <div className="text-muted mb-1">Current Balance</div>
                  <h3 className="mb-0">
                    {formatCurrency(wallet.wallet_balance)}
                  </h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={canRecharge ? "4" : "6"}>
              <Card>
                <CardBody>
                  <div className="text-muted mb-1">Total Recharged</div>
                  <h3 className="mb-0">
                    {formatCurrency(wallet.total_recharged)}
                  </h3>
                </CardBody>
              </Card>
            </Col>
            {canRecharge && (
            <Col md="4">
              <Card>
                <CardBody>
                  <div className="text-muted mb-2">Recharge Wallet</div>
                  <Form onSubmit={handleRecharge}>
                    <FormGroup className="mb-2">
                      <Label for="rechargeAmount" className="visually-hidden">
                        Amount
                      </Label>
                      <Input
                        id="rechargeAmount"
                        type="number"
                        min="1"
                        step="1"
                        placeholder="Amount (₹)"
                        value={rechargeAmount}
                        onChange={(e) => setRechargeAmount(e.target.value)}
                        disabled={recharging}
                      />
                    </FormGroup>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={recharging || !rechargeAmount}
                      block
                    >
                      {recharging ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Recharging...
                        </>
                      ) : (
                        "Recharge"
                      )}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>
            )}
          </Row>

          <Card className="mt-3">
            <CardBody>
              <h5 className="mb-3">Transaction History</h5>
              {history.length === 0 ? (
                <div className="text-muted">No transactions yet</div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-nowrap align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Balance After</th>
                        <th>Policy Number</th>
                        <th>Reason</th>
                        <th>Created By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((txn, index) => (
                        <tr key={`${txn.created_at}-${index}`}>
                          <td>{formatDateTime(txn.created_at)}</td>
                          <td>
                            <Badge
                              color={
                                txn.type === "credit" ? "success" : "danger"
                              }
                              className="text-uppercase"
                            >
                              {txn.type}
                            </Badge>
                          </td>
                          <td>{formatCurrency(txn.amount)}</td>
                          <td>{formatCurrency(txn.balance_after)}</td>
                          <td>{txn.policy_number || "—"}</td>
                          <td>{txn.reason || "—"}</td>
                          <td>{txn.created_by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
              {history.length > 0 && (
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button
                    color="light"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((prev) => prev - 1)}
                  >
                    Previous
                  </Button>
                  <span className="align-self-center small text-muted">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    color="light"
                    size="sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default CompanyWallet;
